"use client";

import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Thread } from "@/components/assistant-ui/thread";
import {
  AssistantRuntime,
  AssistantRuntimeProvider,
} from "@assistant-ui/react";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Pencil, PenLine } from "lucide-react";
import { useNotebookContext } from "../../hooks/useNotebook";

interface ChatInterfaceProps {
  notebookName: string;
  selectedSourcesCount: number;
  totalSourcesCount: number;
  runtime: AssistantRuntime;
  onRenameNotebook?: (name: string) => void;
  showSidebarTrigger?: boolean;
  topBarAction?: React.ReactNode
}

export function ChatInterface({
  notebookName,
  showSidebarTrigger,
  selectedSourcesCount,
  totalSourcesCount,
  runtime,
  onRenameNotebook,
  topBarAction
}: ChatInterfaceProps) {
  const { navigatePage } = useNotebookContext()
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notebookName);

  useEffect(() => {
    setDraft(notebookName);
  }, [notebookName]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft(notebookName);
      return;
    }

    if (trimmed !== notebookName) {
      onRenameNotebook?.(trimmed);
    }

    setEditing(false);
  };

  return (
    <SidebarInset className="h-full flex flex-col">
      {/* Header */}
      <header className="bg-theme-0 sticky top-0 z-10">
        <div className="flex shrink-0 items-center gap-2 h-16">
          <div className="flex flex-1 items-center gap-2 px-3">
            {showSidebarTrigger && (
              <>
                <SidebarTrigger className="text-theme-0" />
                <Separator
                  orientation="vertical"
                  className="mr-2 data-[orientation=vertical]:h-5"
                />
              </>
            )}
            <div className="flex flex-1 items-center justify-between">
              <h1 className="text-sm font-semibold text-theme-0">
                <div className="flex flex-1 items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {editing && showSidebarTrigger ? (
                      <Input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commit();
                          if (e.key === "Escape") {
                            setDraft(notebookName);
                            setEditing(false);
                          }
                        }}
                        className="
          h-7 px-1
          text-sm font-semibold
          bg-transparent
          border-none
          focus-visible:ring-0
          shadow-none
          truncate
          text-theme-0
        "
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="
          group
          flex items-center gap-2
          text-sm font-semibold
          text-theme-0
          truncate
          hover:opacity-90
        "
                      >
                        <span className="truncate">{notebookName}</span>
                        {showSidebarTrigger && (
                          <PenLine className="h-4 w-4 transition" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </h1>
            </div>
            {
              topBarAction && (
                <>
                <div className='flex-grow' />
                {topBarAction}
                </>
              )
            }
          </div>
        </div>
        <Separator />
      </header>

      {/* Chat Thread */}
      <div className="flex-1 overflow-auto">
        {/* EVERY parent of Thread needs to be flex or h-full or else there's a bug where it expands infinitely after the second message */}
        <Thread />
      </div>
    </SidebarInset>
  );
}
